"use client";

import Image from "next/image";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { gdrcrmlogo, premiumsportlight1image, premiumsportlight2image, premiumsportlight3image, problemimage, solutionagenetimage, solutionchartimage } from "@/lib/images";
import { Bath, Bed, Ruler } from "lucide-react";

/**
 * Figma-style four-point star: cardinal points, short flat tips, concave sides (cubic Béziers).
 * Not Lucide `Sparkle` (different silhouette). Uses `currentColor` for badge text match.
 */
function BadgeFourPointStar({ className }: { className?: string }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className={className}
            aria-hidden
        >
            <path d="M11.15 2.35 12.85 2.35C13.5 9 18 11 21.65 11.15L21.65 12.85C18 13 13.5 15 12.85 21.65L11.15 21.65C10.5 15 6 13 2.35 12.85L2.35 11.15C6 11 10.5 9 11.15 2.35z" />
        </svg>
    );
}

const CARD_W = 282;
/** Tailwind `gap-8` → 2rem */
const GAP = 32;

const problemSolutionGlassStyle: CSSProperties = {
    background: "#3333334D, #FFFFFF",
    border: "1px solid #FFFFFF26",
};
const SLIDE_STEP_PX = CARD_W + GAP;
const SPOTLIGHT_SLIDE_INTERVAL_MS = 3000;
const SPOTLIGHT_SLIDE_TRANSITION_MS = 500;

export default function ProblemSolutionGdrDevelopment() {
    const spotlightSliderIndexRef = useRef(0);
    const [spotlightIndex, setSpotlightIndex] = useState(0);
    const [spotlightSliderTransition, setSpotlightSliderTransition] = useState(true);

    useEffect(() => {
        const id = window.setInterval(() => {
            const i = spotlightSliderIndexRef.current;
            if (i < 2) {
                const next = i + 1;
                spotlightSliderIndexRef.current = next;
                setSpotlightIndex(next);
            } else if (i === 2) {
                spotlightSliderIndexRef.current = 3;
                setSpotlightIndex(3);
            }
        }, SPOTLIGHT_SLIDE_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, []);

    useEffect(() => {
        if (spotlightIndex !== 3) return;
        const t = window.setTimeout(() => {
            setSpotlightSliderTransition(false);
            spotlightSliderIndexRef.current = 0;
            setSpotlightIndex(0);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setSpotlightSliderTransition(true);
                });
            });
        }, SPOTLIGHT_SLIDE_TRANSITION_MS);
        return () => window.clearTimeout(t);
    }, [spotlightIndex]);

    return (
        <section className="bg-black xl:py-20 py-6 xl:px-20 px-6">
            <div className="max-w-[1320px] mx-auto">
                <div className=" flex flex-col xl:flex-row  gap-10 lg:gap-17 items-center justify-center ">
                    <div>
                        <Image
                            src={problemimage}
                            alt="Problem Image"
                            className="xl:w-[613px] lg:w-[400px] w-full xl:h-[551px] h-[450px] rounded-[38px] object-cover"
                        />
                    </div>

                    <div>
                        <div
                            className="rounded-[28px] py-6 lg:w-[551px] w-full px-7.5"
                            style={problemSolutionGlassStyle}
                        >
                            <div className="inline-flex items-center gap-2 rounded-full bg-[#FF202045] px-6 py-3 text-base font-medium text-[#FFBEBE] mb-6">
                                <BadgeFourPointStar className="size-5 shrink-0" />
                                Problems
                            </div>

                            <ul className="text-white space-y-6 text-base font-medium">
                                <li className="flex items-start gap-2">
                                    <span
                                        className="mt-2 size-1.5 shrink-0 self-start rounded-full bg-white"
                                        aria-hidden
                                    />
                                    <span>Missed opportunities and slow response time</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span
                                        className="mt-2 size-1.5 shrink-0 self-start rounded-full bg-white"
                                        aria-hidden
                                    />
                                    <span>No Smart Lead Assignment</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span
                                        className="mt-2 size-1.5 shrink-0 self-start rounded-full bg-white"
                                        aria-hidden
                                    />
                                    <span>Property Visit Scheduling Was Messy</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span
                                        className="mt-2 size-1.5 shrink-0 self-start rounded-full bg-white"
                                        aria-hidden
                                    />
                                    <span>No Clear Pipeline</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span
                                        className="mt-2 size-1.5 shrink-0 self-start rounded-full bg-white"
                                        aria-hidden
                                    />
                                    <span>No System for Followups or Automated Workflows</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span
                                        className="mt-2 size-1.5 shrink-0 self-start rounded-full bg-white"
                                        aria-hidden
                                    />
                                    <span>All communication through scattered platforms</span>
                                </li>
                            </ul>
                        </div>
                        <div className="mt-6 flex flex-wrap gap-4 lg:max-w-[551px] w-full">
                            <div className="px-10 py-4 rounded-full bg-[#FFD67847] text-white text-base font-medium">
                                Automation
                            </div>
                            <div className="px-10 py-4 rounded-full bg-[#C7FFB047] text-white text-base font-medium">
                                Distribution
                            </div>
                            <div className="px-10 py-4 rounded-full bg-[#70A5FF47] text-white text-base font-medium">
                                LeadFlow
                            </div>
                            <div className="px-10 py-4 rounded-full bg-[#FFB0D547] text-white text-base font-medium">
                                AutoAssign
                            </div>
                            <div className="px-16 py-4 rounded-full bg-[#ABA2FF47] text-white text-base font-medium">
                                24+ Tools in Custom CRM
                            </div>
                        </div>
                    </div>
                </div>

                {/* Solution section (no flex between left/right, all stacked) */}
                <div className=" flex flex-col xl:flex-row items-center  mx-auto max-w-[1320px] lg:gap-18 gap-6  mt-10">
                    <div className="w-full lg:max-w-[551px] w-full">
                        {/* Left side solution box */}
                        <div className="rounded-[28px] py-6 px-7.5" style={problemSolutionGlassStyle}>
                            <div className="inline-flex items-center gap-2 rounded-full bg-[#6EFF2045] px-6 py-3 text-base font-medium text-[#BEFFC4] mb-6">
                                <BadgeFourPointStar className="size-5 shrink-0" />
                                Solution
                            </div>

                            <ul className="text-white space-y-4 lg:space-y-6 text-base font-medium">
                                <li className="flex items-start gap-2">
                                    <span
                                        className="mt-2 size-1.5 shrink-0 self-start rounded-full bg-white"
                                        aria-hidden
                                    />
                                    <span>Centralized Lead Capture and Faster Response System</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span
                                        className="mt-2 size-1.5 shrink-0 self-start rounded-full bg-white"
                                        aria-hidden
                                    />
                                    <span>Automated Lead Assignment and Smart Distribution</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span
                                        className="mt-2 size-1.5 shrink-0 self-start rounded-full bg-white"
                                        aria-hidden
                                    />
                                    <span>Integrated Property Visit Booking Calendar</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span
                                        className="mt-2 size-1.5 shrink-0 self-start rounded-full bg-white"
                                        aria-hidden
                                    />
                                    <span>Visual Sales Pipeline and Deal Tracking Dashboard</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span
                                        className="mt-2 size-1.5 shrink-0 self-start rounded-full bg-white"
                                        aria-hidden
                                    />
                                    <span>Automated Follow-ups and Workflow Automation</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span
                                        className="mt-2 size-1.5 shrink-0 self-start rounded-full bg-white"
                                        aria-hidden
                                    />
                                    <span>Unified Communication and Collaboration Platform</span>
                                </li>
                            </ul>
                        </div>

                        {/* 3 boxes outside the main card */}
                        <div className="mt-7 space-y-7">
                            <div className="flex-1 rounded-[28px] border border-[#FFFFFF26] bg-[#0B0B0B33] h-[90px] px-3 sm:px-4 md:px-5 lg:px-6 flex items-center justify-center">
                                <Image src={gdrcrmlogo} alt="GDR CRM Logo" className="w-[190px] sm:w-[240px] md:w-[300px] lg:w-[360px] xl:w-[437px] h-[24px] sm:h-[30px] md:h-[36px] lg:h-[42px] xl:h-[50px] max-w-full object-contain" />
                            </div>
                            <div className="flex-1 rounded-[28px] border border-[#FFFFFF26] bg-[#0B0B0B33] text-center xl:text-3xl text-2xl font-medium text-white h-[90px] flex items-center justify-center">
                                Project Duration: 3-4 Months
                            </div>
                            <div className="flex-1 rounded-[28px] border border-[#FFFFFF26] bg-[#0B0B0B33] text-center xl:text-3xl text-2xl font-medium text-white h-[90px] flex items-center justify-center">
                                Real Estate CRM + Website
                            </div>
                        </div>
                    </div>
                    <div className="relative w-full lg:max-w-[551px] flex flex-col lg:p-8 p-2 min-h-[120px]">
                        <div
                            className="pointer-events-none absolute inset-y-12 left-0 z-0 w-[44px] overflow-hidden [clip-path:inset(0_round_28px_0_0_28px)] bg-[url('/images/case-studies/gdr-development/solution-bg-image.png')] bg-center bg-no-repeat bg-cover"
                            aria-hidden
                        >
                           
                        </div>
                        <div className="relative z-10 flex flex-col gap-6">
                            <div className="flex sm:flex-row flex-col gap-6">
                                <Image src={solutionchartimage} alt="GDR Development Image" className="sm:w-[310px] w-full h-[323px]   border border-[#4D4D4D] rounded-[14px]" />
                                <Image src={solutionagenetimage} alt="GDR Development Image" className="sm:w-[203px] w-full sm:h-[183px] h-[300px]  border border-[#4D4D4D] rounded-[14px]" />
                            </div>
                            <div className="relative border-t border-[#4D4D4D] border-r-0 border-b-0 rounded-tl-[14px] p-7 bg-black overflow-hidden lg:w-[565px] w-full">
                                {/* Gradient left edge (Tailwind can't apply gradient to `border-*` directly). */}
                                <div
                                    aria-hidden
                                    className="pointer-events-none absolute left-0 top-0 bottom-0 w-px bg-linear-to-b from-[#4D4D4D] to-transparent rounded-tl-[14px]"
                                />
                                <h4 className="font-bold text-[20px] text-[#DAE2FD]">Premium Listings Spotlight</h4>
                                <div
                                    className="flex  gap-8 mt-6"
                                    style={{
                                        transform: `translateX(-${spotlightIndex * SLIDE_STEP_PX}px)`,
                                        transition: spotlightSliderTransition ? "transform 0.5s ease-out" : "none",
                                    }}
                                >
                                    {/* Card 1 (image with badges; text shown below image, outside) */}
                                    
                                        <div>
                                            <div className="relative sm:h-[211px] h-[150px] w-[282px] rounded-[28px] overflow-hidden">
                                                <Image src={premiumsportlight1image} alt="GDR Development Image" fill className="object-cover opacity-60" />
                                                <div className="absolute top-3 left-3 flex gap-2">
                                                    <span className="rounded-full bg-[#404B6E] text-[11px] font-semibold tracking-[0.12em] text-white px-3.5 py-1.5 uppercase">Premium</span>
                                                    <span className="rounded-full bg-[#9BF52C] text-[11px] font-semibold tracking-[0.12em] text-black px-3.5 py-1.5 uppercase">Active</span>
                                                </div>
                                                <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                                                    <span className="text-white text-[22px] font-semibold">$12,450,000</span>
                                                    <span className="h-10 w-10 rounded-[12px] bg-[#2D344966] border border-[#8B947F1A] flex items-center justify-center">
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 21s-7.5-4.35-9.33-8.62C1.3 9.93 3.36 6.5 6.9 6.5c2.04 0 3.39 1.04 4.1 2.08.71-1.04 2.06-2.08 4.1-2.08 3.54 0 5.6 3.43 4.23 5.88C19.5 16.65 12 21 12 21z" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="mt-4">
                                                <p className="text-white font-semibold text-[18px]">The Obsidian Estate</p>
                                                <p className="mt-1 flex items-center gap-2 text-sm text-[#C0CAB3]">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 21s-6-6.7-6-10.5S9.13 3 12 3s6 2.47 6 7.5S12 21 12 21z" stroke="#9BF52C" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="10.5" r="2.2" fill="#9BF52C" /></svg>
                                                    Sunny Shores, Florida
                                                </p>
                                                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-medium text-[#C0CAB3]">
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Bed className="h-4 w-4 shrink-0 text-[#C0CAB3]" strokeWidth={1.75} />
                                                        6 Beds
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Bath className="h-4 w-4 shrink-0 text-[#C0CAB3]" strokeWidth={1.75} />
                                                        8 Baths
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Ruler className="h-4 w-4 shrink-0 text-[#C0CAB3]" strokeWidth={1.75} />
                                                        8,200 sqft
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    

                                    {/* Card 2 */}
                                    
                                        <div>
                                            <div className="relative sm:h-[211px] h-[150px] w-[282px] rounded-[28px] overflow-hidden">
                                                <Image src={premiumsportlight2image} alt="GDR Development Image" fill className="object-cover opacity-60" />
                                                <div className="absolute top-3 left-3">
                                                    <span className="rounded-full bg-[#404B6E] text-[11px] font-semibold tracking-[0.12em] text-white px-3.5 py-1.5 uppercase">New Listing</span>
                                                </div>
                                                <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                                                    <span className="text-white text-[22px] font-semibold">$8,900,000</span>
                                                    <span className="h-10 w-10 rounded-[12px] bg-[#2D344966] border border-[#8B947F1A] flex items-center justify-center shrink-0">
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                                            <path
                                                                d="M12 21s-7.5-4.35-9.33-8.62C1.3 9.93 3.36 6.5 6.9 6.5c2.04 0 3.39 1.04 4.1 2.08.71-1.04 2.06-2.08 4.1-2.08 3.54 0 5.6 3.43 4.23 5.88C19.5 16.65 12 21 12 21z"
                                                                stroke="white"
                                                                strokeWidth="1.6"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="mt-4">
                                                <p className="text-white font-semibold text-[18px]">706, Kanuga Dr</p>
                                                <p className="mt-1 flex items-center gap-2 text-sm text-[#C0CAB3]">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 21s-6-6.7-6-10.5S9.13 3 12 3s6 2.47 6 7.5S12 21 12 21z" stroke="#9BF52C" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="10.5" r="2.2" fill="#9BF52C" /></svg>
                                                    Miami, Florida
                                                </p>
                                                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-medium text-[#C0CAB3]">
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Bed className="h-4 w-4 shrink-0 text-[#C0CAB3]" strokeWidth={1.75} />
                                                        4 Beds
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Bath className="h-4 w-4 shrink-0 text-[#C0CAB3]" strokeWidth={1.75} />
                                                        5 Baths
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Ruler className="h-4 w-4 shrink-0 text-[#C0CAB3]" strokeWidth={1.75} />
                                                        4,500 sqft
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    

                                    {/* Card 3 — Harbor Point Villa */}
                                    
                                        <div>
                                            <div className="relative sm:h-[211px] h-[150px] w-[282px] rounded-[28px] overflow-hidden">
                                                <Image src={premiumsportlight3image} alt="Harbor Point Villa preview" fill className="object-cover opacity-60" />
                                                <div className="absolute top-3 left-3">
                                                    <span className="rounded-full bg-[#404B6E] text-[11px] font-semibold tracking-[0.12em] text-white px-3.5 py-1.5 uppercase">
                                                        Exclusive
                                                    </span>
                                                </div>
                                                <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                                                    <span className="text-white text-[22px] font-semibold">$15,200,000</span>
                                                    <span className="h-10 w-10 rounded-[12px] bg-[#2D344966] border border-[#8B947F1A] flex items-center justify-center shrink-0">
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                                            <path
                                                                d="M12 21s-7.5-4.35-9.33-8.62C1.3 9.93 3.36 6.5 6.9 6.5c2.04 0 3.39 1.04 4.1 2.08.71-1.04 2.06-2.08 4.1-2.08 3.54 0 5.6 3.43 4.23 5.88C19.5 16.65 12 21 12 21z"
                                                                stroke="white"
                                                                strokeWidth="1.6"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="mt-4">
                                                <p className="text-white font-semibold text-[18px]">Harbor Point Villa</p>
                                                <p className="mt-1 flex items-center gap-2 text-sm text-[#C0CAB3]">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                        <path
                                                            d="M12 21s-6-6.7-6-10.5S9.13 3 12 3s6 2.47 6 7.5S12 21 12 21z"
                                                            stroke="#9BF52C"
                                                            strokeWidth="1.6"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                        <circle cx="12" cy="10.5" r="2.2" fill="#9BF52C" />
                                                    </svg>
                                                    Miami Beach, Florida
                                                </p>
                                                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-medium text-[#C0CAB3]">
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Bed className="h-4 w-4 shrink-0 text-[#C0CAB3]" strokeWidth={1.75} />
                                                        7 Beds
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Bath className="h-4 w-4 shrink-0 text-[#C0CAB3]" strokeWidth={1.75} />
                                                        9 Baths
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Ruler className="h-4 w-4 shrink-0 text-[#C0CAB3]" strokeWidth={1.75} />
                                                        12,000 sqft
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    

                                    {/* Dupes for seamless loop: after slide 3 (index 2) advance to same layout as start */}
                                    <div className="shrink-0 w-[282px]">
                                        <div className="flex-1">
                                            <div className="relative sm:h-[211px] h-[150px] w-[282px] rounded-[28px] overflow-hidden">
                                                <Image src={premiumsportlight1image} alt="GDR Development Image" fill className="object-cover opacity-60" />
                                                <div className="absolute top-3 left-3 flex gap-2">
                                                    <span className="rounded-full bg-[#404B6E] text-[11px] font-semibold tracking-[0.12em] text-white px-3.5 py-1.5 uppercase">Premium</span>
                                                    <span className="rounded-full bg-[#9BF52C] text-[11px] font-semibold tracking-[0.12em] text-black px-3.5 py-1.5 uppercase">Active</span>
                                                </div>
                                                <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                                                    <span className="text-white text-[22px] font-semibold">$12,450,000</span>
                                                    <span className="h-10 w-10 rounded-[12px] bg-[#2D344966] border border-[#8B947F1A] flex items-center justify-center">
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 21s-7.5-4.35-9.33-8.62C1.3 9.93 3.36 6.5 6.9 6.5c2.04 0 3.39 1.04 4.1 2.08.71-1.04 2.06-2.08 4.1-2.08 3.54 0 5.6 3.43 4.23 5.88C19.5 16.65 12 21 12 21z" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="mt-4">
                                                <p className="text-white font-semibold text-[18px]">The Obsidian Estate</p>
                                                <p className="mt-1 flex items-center gap-2 text-sm text-[#C0CAB3]">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 21s-6-6.7-6-10.5S9.13 3 12 3s6 2.47 6 7.5S12 21 12 21z" stroke="#9BF52C" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="10.5" r="2.2" fill="#9BF52C" /></svg>
                                                    Sunny Shores, Florida
                                                </p>
                                                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-medium text-[#C0CAB3]">
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Bed className="h-4 w-4 shrink-0 text-[#C0CAB3]" strokeWidth={1.75} />
                                                        6 Beds
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Bath className="h-4 w-4 shrink-0 text-[#C0CAB3]" strokeWidth={1.75} />
                                                        8 Baths
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Ruler className="h-4 w-4 shrink-0 text-[#C0CAB3]" strokeWidth={1.75} />
                                                        8,200 sqft
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="shrink-0 w-[282px]">
                                        <div className="flex-1">
                                            <div className="relative sm:h-[211px] h-[180px] w-[282px] rounded-[28px] overflow-hidden">
                                                <Image src={premiumsportlight2image} alt="GDR Development Image" fill className="object-cover opacity-60" />
                                                <div className="absolute top-3 left-3">
                                                    <span className="rounded-full bg-[#404B6E] text-[11px] font-semibold tracking-[0.12em] text-white px-3.5 py-1.5 uppercase">New Listing</span>
                                                </div>
                                                <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                                                    <span className="text-white text-[22px] font-semibold">$8,900,000</span>
                                                    <span className="h-10 w-10 rounded-[12px] bg-[#2D344966] border border-[#8B947F1A] flex items-center justify-center shrink-0">
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                                            <path
                                                                d="M12 21s-7.5-4.35-9.33-8.62C1.3 9.93 3.36 6.5 6.9 6.5c2.04 0 3.39 1.04 4.1 2.08.71-1.04 2.06-2.08 4.1-2.08 3.54 0 5.6 3.43 4.23 5.88C19.5 16.65 12 21 12 21z"
                                                                stroke="white"
                                                                strokeWidth="1.6"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="mt-4">
                                                <p className="text-white font-semibold text-[18px]">706, Kanuga Dr</p>
                                                <p className="mt-1 flex items-center gap-2 text-sm text-[#C0CAB3]">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 21s-6-6.7-6-10.5S9.13 3 12 3s6 2.47 6 7.5S12 21 12 21z" stroke="#9BF52C" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="10.5" r="2.2" fill="#9BF52C" /></svg>
                                                    Miami, Florida
                                                </p>
                                                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-medium text-[#C0CAB3]">
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Bed className="h-4 w-4 shrink-0 text-[#C0CAB3]" strokeWidth={1.75} />
                                                        4 Beds
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Bath className="h-4 w-4 shrink-0 text-[#C0CAB3]" strokeWidth={1.75} />
                                                        5 Baths
                                                    </span>
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Ruler className="h-4 w-4 shrink-0 text-[#C0CAB3]" strokeWidth={1.75} />
                                                        4,500 sqft
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}